import { StatCard } from "@c/shared";
import { cn } from "@u/cn";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Minus,
  type LucideIcon,
} from "lucide-react";
import { type ReactNode } from "react";

export interface DashboardTrend {
  /** Percent change; sign drives up/down color + arrow. */
  delta: number;
  label?: string;
}

export interface DashboardStatsProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  /** Info icon next to the label; explanation shown in a tooltip. */
  info?: string;
  trend?: DashboardTrend;
  comparison?: { previousLabel: string; previousValue: ReactNode };
  subtext?: string;
  subtextLink?: string;
  progress?: { value: number; max: number | null };
  density?: "default" | "compact";
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

function TrendBadge({ delta, label }: DashboardTrend) {
  const up = delta > 0;
  const down = delta < 0;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const tone = up
    ? "text-success-strong"
    : down
      ? "text-destructive-strong"
      : "text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wide",
        tone,
      )}
    >
      <Icon size={12} />
      {delta > 0 ? "+" : ""}
      {delta}%{label ? ` ${label}` : ""}
    </span>
  );
}

/**
 * KPI stat — composes the canonical `StatCard` and layers an optional trend
 * badge or comparison line. "Variants": plain (value only), icon (pass icon),
 * trend (pass trend), comparison (pass comparison).
 */
export function DashboardStats({
  label,
  value,
  icon,
  info,
  trend,
  comparison,
  subtext,
  subtextLink,
  progress,
  density,
  onClick,
  active,
  className,
}: DashboardStatsProps) {
  const sub: ReactNode = trend ? (
    <TrendBadge {...trend} />
  ) : comparison ? (
    `${comparison.previousLabel} ${comparison.previousValue}`
  ) : (
    subtext
  );

  return (
    <StatCard
      icon={icon ?? BarChart3}
      label={label}
      info={info}
      value={value}
      subtext={sub}
      subtextLink={trend || comparison ? undefined : subtextLink}
      progress={progress}
      density={density}
      onClick={onClick}
      active={active}
      className={className}
    />
  );
}
